import React, { type ReactElement, type ReactNode } from "react";
import { Button, cn } from "@ndd/ui";
import { THEMED_QUIET_LINK } from "../../lib/always-look";
import {
  MARK_CALLED_FAILED_MESSAGE,
  RED_FLAG_READ_FAILED_MESSAGE,
} from "../../lib/patient-red-flags-client";

// The patient page's red-flag banner (NO-850), below the header: the Client's
// open critical clinical red flag, a link to the appointment the flagged
// Session belongs to, and Mark called. Red because it is a problem the RD
// fixes in-app (AGENTS.md colour rules). A plain region, not role="alert":
// it is page content present on load, not an interruption. "Open the
// screening questions" waits on clinical leadership's approved questions.
// A leaf module (`import React`, relative imports, no router, no queries) so
// `tsx --test` renders it; the page supplies the link and the click.

export type RenderAppointmentLink = (props: {
  appointmentId: string;
  className: string;
  children: ReactNode;
}) => ReactElement;

const BANNER = cn(
  "flex flex-wrap items-center gap-x-6 gap-y-3 rounded-lg border border-destructive bg-danger-soft px-6 py-4 text-body text-foreground",
  "brand:rounded-xl brand:border-foreground"
);

const QUIET_LINK = cn(
  "shrink-0 text-body font-semibold text-foreground underline underline-offset-4",
  THEMED_QUIET_LINK
);

export function PatientRedFlagBanner({
  flagText,
  appointmentId,
  renderAppointmentLink,
  onMarkCalled,
  marking = false,
  markFailed = false,
}: {
  flagText: string;
  appointmentId: string | null;
  renderAppointmentLink: RenderAppointmentLink;
  onMarkCalled?: () => void;
  marking?: boolean;
  markFailed?: boolean;
}): ReactElement {
  return (
    <section role="region" aria-label="Red flag" data-red-flag="" className={BANNER}>
      <p className="min-w-0 flex-1">
        <span className="font-semibold">{`Red flag: ${flagText}.`}</span> Call today and
        screen.
      </p>
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
        {appointmentId
          ? renderAppointmentLink({
              appointmentId,
              className: QUIET_LINK,
              children: "Open the appointment",
            })
          : null}
        {onMarkCalled ? (
          <Button
            type="button"
            variant="press"
            shape="pill"
            disabled={marking}
            onClick={onMarkCalled}
          >
            Mark called
          </Button>
        ) : null}
      </div>
      {markFailed ? (
        <p role="status" className="basis-full text-body">
          {MARK_CALLED_FAILED_MESSAGE}
        </p>
      ) : null}
    </section>
  );
}

/** A read that failed: a neutral line and a way to try again, never an alarm. */
export function PatientRedFlagReadFailed({
  onRetry,
}: {
  onRetry: () => void;
}): ReactElement {
  return (
    <div
      data-red-flag-failed=""
      className="flex flex-wrap items-center gap-x-4 gap-y-1"
    >
      <p className="text-body text-muted-foreground">{RED_FLAG_READ_FAILED_MESSAGE}</p>
      <Button
        type="button"
        variant="quiet-link"
        className="h-auto px-0 py-0 text-body"
        onClick={onRetry}
      >
        Try again
      </Button>
    </div>
  );
}
